import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import {
  makeHasSelectedRecommendation,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectGlobalField,
} from 'Containers/App/selectors';
import { useBcHandoffLogin } from 'Containers/Auth/AuthButton';
import { makeGetRecommendedHsaContribution } from 'Containers/CommercialRoutes/selectors';
import { makeSelectHsaField } from 'Containers/HsaPage/selectors';
import { makeSelectOverviewField } from 'Containers/OverviewPage/selectors';
import {
  makeGetBuilderGoSetting,
  makeGetSelectedProduct,
  makeGetSelectedPublicationPlanYear,
  makeGetSelectedPublicationSegmentSlug,
  makeGetTierCodeData,
  makeSelectProfileField,
} from 'Containers/ProfilePage/selectors';
import { ClaimsFormStatus } from 'Containers/ProfilePage/types';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useProfileContext } from 'Contexts/profileContext';
import { FadeUp } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { Recommendation, SelectedSupplementalPlans } from 'Types/entities';
import { GetHsaRecommendationResponse } from 'Utils/apiTypes';
import {
  ASK_ALEX_PATH,
  ERROR_PATH,
  getBCPath,
  getEnvironmentFromUrl,
  HSA_PLUS_PATH,
  INTRO_PATH,
  MTM_PATH,
  OVERVIEW_PATH,
  PROFILE_PATH,
  RESULT_PATH,
  VIEW_PLAN_PATH,
  WELCOME_PATH,
} from 'Utils/urls';

import { Buttons } from './Buttons';
import Profile from './Profile';
import { Results } from './Results';
import { ButtonsWrapper, CtaContainer, CtaHalo, CtaPosition } from './styled';
import { DEPLOY_ENVIRONMENTS } from '../../../constants';
import { GO_GUEST_ID } from '../constants';
import { AlexProducts, JVPState } from '../types';
import { handleBcNavigate, takeBcHandoffResume } from '../utils';

export interface CtaBarParentProps {
  isBuilderClientWithNoEligiblePlans: boolean;
  hasUnsavedProfileChanges: boolean;
}

export interface CtaBarStoreProps {
  selectedPlan: Recommendation | Record<string, never>;
  isCoverageWaived: boolean;
  selectedSupplementalPlans: SelectedSupplementalPlans;
  hsaData: GetHsaRecommendationResponse | Record<string, never>;
  fbsEnabled: boolean;
  hsaEnabled: boolean;
  taxSavingsEnabled: boolean;
  killMedRec: boolean;
  lockedHsaContribution: number | null;
  hsaContribution: number | null;
  recommendedHsaContribution: number | null;
  householdDataChanged: boolean;
  isIncentiveLoading: boolean;
  isEnrollmentLoading: boolean;
  hasSelectedRecommendation: boolean;
  isIntegratedUser: boolean;
  jvp: JVPState;
  isDemoMode: boolean;
  planYear: number | undefined;
  validatedSegmentSlug: string | undefined;
  bcUrlHash: string;
  selectedProduct: AlexProducts | '';
  claimsFormStatus: ClaimsFormStatus;
  claimsSummaryPolling: boolean;
  tierCode: string[];
}

export type CtaBarProps = CtaBarParentProps & CtaBarStoreProps;

export const CtaBar = ({
  isBuilderClientWithNoEligiblePlans,
  selectedPlan,
  isCoverageWaived,
  selectedSupplementalPlans,
  fbsEnabled,
  hsaEnabled,
  taxSavingsEnabled,
  killMedRec,
  hsaData,
  lockedHsaContribution,
  hsaContribution,
  recommendedHsaContribution,
  isIncentiveLoading,
  isEnrollmentLoading,
  hasUnsavedProfileChanges,
  householdDataChanged,
  hasSelectedRecommendation,
  isIntegratedUser,
  jvp,
  isDemoMode,
  planYear,
  validatedSegmentSlug,
  bcUrlHash,
  selectedProduct,
  claimsFormStatus,
  claimsSummaryPolling,
  tierCode,
}: CtaBarProps) => {
  const { getIsFormValid, getIsFormCompleted, reviewMode } = useProfileContext();

  // This captures the period of time when a household data change has occurred,
  // and the form is valid & complete. This involves mixed Redux and ProfileContext state,
  // so to avoid flashes of rendering the buttons before loading occurs, we derive the
  // value here. householdDataChanged + a valid and complete form should always trigger
  // a household api request, which should always result in householdDataChanged
  // becoming false, whether the request succeeds or fails.
  const isHouseholdDataLoading = householdDataChanged && getIsFormValid() && getIsFormCompleted();
  const isLoading = isIncentiveLoading || isEnrollmentLoading || isHouseholdDataLoading;
  const { pathname } = useLocation();
  const path = pathname;
  const selectedProductIsBc = selectedProduct === 'bc';
  const { isAuthenticated, isLoading: isAuth0Loading, getIdTokenClaims } = useAuth0();
  const bcHandoffLogin = useBcHandoffLogin({ resumeAfterLogin: true });

  const [isHsaUnlocked, setIsHsaUnlocked] = useState(false);
  const [isSupplementalUnlocked, setIsSupplementalUnlocked] = useState(false);

  // Logic to show Halo Glow effect
  const isFormValid = getIsFormValid();
  const isFormUnlocked = getIsFormCompleted();
  const showProfileGlow = path === PROFILE_PATH && isFormValid && isFormUnlocked;
  const showHealthGlow =
    (path === RESULT_PATH || path === VIEW_PLAN_PATH) && (hasSelectedRecommendation || isCoverageWaived);
  const showGlow = showProfileGlow || showHealthGlow;
  const { is_profile_review_tool_enabled } = useFeatureFlagContext();

  useEffect(() => {
    if (path === HSA_PLUS_PATH && !isHsaUnlocked) {
      setIsHsaUnlocked(true);
    }
    if (path === OVERVIEW_PATH && !isSupplementalUnlocked) {
      setIsSupplementalUnlocked(true);
    }
  }, [path]);

  const handleBcRedirect = () => {
    handleBcNavigate(
      jvp,
      isDemoMode,
      planYear,
      validatedSegmentSlug,
      bcUrlHash,
      GO_GUEST_ID,
      isAuthenticated,
      getIdTokenClaims,
      getBCPath,
      bcHandoffLogin,
    );
  };

  // Continues the trip to BC that a re-login interrupted, once Go has the fresh login.
  useEffect(() => {
    if (!isAuth0Loading && isAuthenticated && takeBcHandoffResume()) {
      handleBcRedirect();
    }
  }, [isAuth0Loading, isAuthenticated]);

  if (
    !path ||
    path === WELCOME_PATH ||
    path === MTM_PATH ||
    path === ERROR_PATH ||
    path === ASK_ALEX_PATH ||
    (path !== PROFILE_PATH && hasUnsavedProfileChanges) ||
    (path === RESULT_PATH && selectedProductIsBc && !reviewMode)
  ) {
    return null;
  }

  return (
    <CtaPosition>
      <FadeUp delay={4000} isOpen={path !== WELCOME_PATH && path !== INTRO_PATH}>
        <PageLayout bg="--primary-red" page="cta-bar">
          <Box pos="relative" zIndex={0}>
            <CtaContainer data-testid="cta-container" profile={path === PROFILE_PATH ? 1 : 0}>
              {path === PROFILE_PATH ? (
                <Profile claimsSummaryPolling={claimsSummaryPolling} claimsFormStatus={claimsFormStatus} />
              ) : (
                <Results
                  isHsaUnlocked={isHsaUnlocked}
                  isSupplementalUnlocked={isSupplementalUnlocked}
                  selectedPlan={selectedPlan}
                  isCoverageWaived={isCoverageWaived || isBuilderClientWithNoEligiblePlans}
                  selectedSupplementalPlans={selectedSupplementalPlans}
                  hsaContribution={hsaContribution}
                  recommendedHsaContribution={recommendedHsaContribution}
                  killMedRec={killMedRec}
                />
              )}
              <ButtonsWrapper>
                {is_profile_review_tool_enabled &&
                !isIntegratedUser &&
                !selectedProductIsBc &&
                path === PROFILE_PATH &&
                getEnvironmentFromUrl() !== DEPLOY_ENVIRONMENTS.PROD ? (
                  <Buttons
                    isLoading={isLoading}
                    selectedPlan={selectedPlan}
                    isCoverageWaived={isCoverageWaived}
                    selectedSupplementalPlans={selectedSupplementalPlans}
                    fbsEnabled={fbsEnabled}
                    hsaEnabled={hsaEnabled}
                    taxSavingsEnabled={taxSavingsEnabled}
                    hsaData={hsaData}
                    lockedHsaContribution={lockedHsaContribution}
                    buttonText="profileReview"
                    dataTestId="cta-review-button"
                  />
                ) : null}

                <Buttons
                  isLoading={isLoading}
                  selectedPlan={selectedPlan}
                  isCoverageWaived={isCoverageWaived}
                  selectedSupplementalPlans={selectedSupplementalPlans}
                  fbsEnabled={fbsEnabled}
                  hsaEnabled={hsaEnabled}
                  taxSavingsEnabled={taxSavingsEnabled}
                  hsaData={hsaData}
                  lockedHsaContribution={lockedHsaContribution}
                  handleBcRedirect={handleBcRedirect}
                  selectedProductIsBc={selectedProductIsBc}
                  isIntegratedUser={isIntegratedUser}
                  tierCode={tierCode}
                />
              </ButtonsWrapper>
            </CtaContainer>

            {showGlow && <CtaHalo />}
          </Box>
        </PageLayout>
      </FadeUp>
    </CtaPosition>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, CtaBarStoreProps>({
  selectedPlan: makeSelectCommercialField('selectedHealthPlan'),
  isCoverageWaived: makeSelectCommercialField('isCoverageWaived'),
  selectedSupplementalPlans: makeSelectOverviewField('selected_plans'),
  recommendedHsaContribution: makeGetRecommendedHsaContribution(),
  lockedHsaContribution: makeSelectHsaField('lockedHsaContribution'),
  hsaData: makeSelectHsaField('hsaData'),
  fbsEnabled: makeSelectConfigField('is_fbs_enabled'),
  hsaEnabled: makeSelectConfigField('hsa_plus_enabled'),
  taxSavingsEnabled: makeSelectConfigField('show_tax_savings_page'),
  killMedRec: makeGetBuilderGoSetting('kill_med_rec'),
  hsaContribution: makeSelectHsaField('lockedHsaContribution'),
  isIncentiveLoading: makeSelectProfileField('incentiveSurveyIsLoading'),
  isEnrollmentLoading: makeSelectCommercialField('isEnrollmentLoading'),
  hasSelectedRecommendation: makeHasSelectedRecommendation(),
  isIntegratedUser: makeSelectConfigField('is_integrated'),
  householdDataChanged: makeSelectProfileField('householdDataChanged'),
  jvp: makeSelectGlobalField('jvp'),
  isDemoMode: makeSelectGlobalField('isDemoMode'),
  planYear: makeGetSelectedPublicationPlanYear(),
  validatedSegmentSlug: makeGetSelectedPublicationSegmentSlug(),
  bcUrlHash: makeSelectGlobalField('bcUrlHash'),
  selectedProduct: makeGetSelectedProduct(),
  claimsFormStatus: makeSelectProfileField('claimsFormStatus'),
  claimsSummaryPolling: makeSelectProfileField('claimsSummaryPolling'),
  tierCode: makeGetTierCodeData(),
});

const withConnect = connect(mapStateToProps);
export default compose(withConnect)(CtaBar);
